<template>
    <div class="relative bg-gray-800 ring-1 ring-black/10 rounded-lg shadow-sm overflow-hidden">
        <div class="bg-gray-900/25 border-b border-gray-600 px-4 py-3.5">
            <div class="flex items-center justify-between">
                <div class="space-y-1">
                    <h3 class="leading-none text-sm text-gray-100">Call <span class="font-mono">#{{ report.call?.number }}</span></h3>
                    <p class="leading-none text-xs text-gray-400">{{ dayjs(report.updated_at).format('MMM D, YYYY HH:mm') }}</p>
                </div>
                <div class="flex shrink-0 items-center space-x-2">
                    <UBadge v-if="!isRegularReport" label="Feedback" size="sm" color="orange" variant="subtle" />
                    <template v-else>
                        <UBadge v-if="report.call?.id === callId" label="Current" size="sm" color="blue" variant="subtle" />
                        <UBadge v-if="report.user_id === auth.user?.id" label="Your Report" size="sm" color="green" variant="subtle" />
                    </template>
                </div>
            </div>
        </div>
        <div class="space-y-3 p-4 pt-3">
            <div v-if="isRegularReport">
                <p class="text-[11px] uppercase leading-5 text-gray-400">Reason for Calling</p>
                <p v-if="report.call_reason" class="text-sm text-gray-100">{{ report.call_reason.label }}</p>
                <p v-else class="text-sm text-gray-400">--</p>
            </div>
            <div>
                <p class="text-[11px] uppercase leading-5 text-gray-400">Notes</p>
                <p v-if="report.notes" class="text-sm text-gray-100 whitespace-pre-line">{{ report.notes }}</p>
                <p v-else class="text-sm text-gray-400">--</p>
            </div>
            <div v-if="isRegularReport && (report.user.role === UserRole.medicalDirector || report.user.role === UserRole.doctor)">
                <p class="text-[11px] uppercase leading-5 text-gray-400">Recommendation</p>
                <p v-if="report.consultation" class="text-sm text-gray-100 whitespace-pre-line">{{ report.consultation }}</p>
                <p v-else class="text-sm text-gray-400">--</p>
            </div>
            <div v-if="isRegularReport">
                <p class="text-[11px] uppercase leading-5 text-gray-400">Suspected Diagnosis</p>
                <div v-if="report.diagnosis" class="flex items-center space-x-2">
                    <p class="text-sm text-gray-100">{{ report.diagnosis.label }}</p>
                    <BadgePriority small v-if="report.diagnosis.metadata?.priority" :priority="report.diagnosis.metadata.priority" />
                </div>
                <p v-else class="text-sm text-gray-400">--</p>
            </div>
            <div v-if="isRegularReport">
                <p class="text-[11px] uppercase leading-5 text-gray-400">Final Decision</p>
                <p v-if="report.call_decision" class="text-sm text-gray-100">{{ report.call_decision.label }}</p>
                <p v-else class="text-sm text-gray-400">--</p>
            </div>
            <div>
                <p class="text-[11px] uppercase leading-5 text-gray-400">Submitted By</p>
                <div class="flex items-center space-x-2">
                    <p class="text-sm text-gray-100">{{ report.user.name }}</p>
                    <BadgeUserRole minimal :role="report.user.role" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { FEEDBACK_REPORT_REASON_ID } from '~/types/call';
import type { CallReport } from '~/types/callReport'
import { UserRole } from '~/types/user';

const props = defineProps<{
    callId: string
    report: CallReport
}>()

const auth = useAuthStore()

const isRegularReport = computed(() => props.report?.call_reason?.id !== FEEDBACK_REPORT_REASON_ID)
</script>
